// Workbench docket — pure state machine (no React, no fetch).
//
// Honesty contract mirrors the backend: check lights only change after a
// REAL check run; local edits stale the lights (the UI shows them dimmed)
// until the next run. `newlyPassed` names exactly the checks that flipped
// to green so the panel can give those — and only those — the system's
// single "rise" motion.

export const initialState = {
  phase: 'loading', // loading | active | submitting | submitted | error
  session: null,
  selectedPath: null,
  contents: {}, // path -> live editor content
  dirty: {}, // path -> true while unsaved
  results: {}, // check id -> passed (last real run)
  lightsStale: false,
  grades: null,
  feedback: null,
  solved: null,
  error: null,
};

function firstEditablePath(session) {
  const f = (session.files || []).find((x) => !x.read_only) || session.files?.[0];
  return f ? f.path : null;
}

export function reducer(state, action) {
  switch (action.type) {
    case 'loaded': {
      const contents = {};
      for (const f of action.session.files) contents[f.path] = f.content;
      return {
        ...initialState,
        phase: 'active',
        session: action.session,
        selectedPath: firstEditablePath(action.session),
        contents,
      };
    }
    case 'load_failed':
      return { ...state, phase: 'error', error: action.error };
    case 'select_file':
      return { ...state, selectedPath: action.path };
    case 'edited': {
      const file = state.session?.files.find((f) => f.path === action.path);
      if (!file || file.read_only) return state;
      return {
        ...state,
        contents: { ...state.contents, [action.path]: action.content },
        dirty: { ...state.dirty, [action.path]: true },
        lightsStale: true,
      };
    }
    case 'saved': {
      const dirty = { ...state.dirty };
      delete dirty[action.path];
      return { ...state, dirty };
    }
    case 'save_failed':
      return { ...state, error: action.error };
    case 'checks_started':
      return { ...state, checking: true, error: null };
    case 'checks_landed': {
      const results = { ...state.results };
      for (const r of action.results) results[r.check_id] = r.passed;
      return { ...state, checking: false, results, lightsStale: false };
    }
    case 'checks_failed':
      return { ...state, checking: false, error: action.error };
    case 'submit_started':
      return { ...state, phase: 'submitting', error: null };
    case 'submitted': {
      const results = { ...state.results };
      for (const r of action.results || []) results[r.check_id] = r.passed;
      return {
        ...state,
        phase: 'submitted',
        grades: action.grades,
        feedback: action.feedback,
        solved: action.solved,
        results,
        lightsStale: false,
      };
    }
    case 'submit_failed':
      return { ...state, phase: 'active', error: action.error };
    default:
      return state;
  }
}

// Which checks flipped to green between two result maps?
export function newlyPassed(before, after) {
  return Object.keys(after).filter((id) => after[id] === true && before[id] !== true);
}
